import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const cardVariants = { hidden: { opacity: 0, y: 40, scale: 0.95 }, visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: "easeOut" } } };

function TransformationCard({ item, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ y: -6, scale: 1.02 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="bg-gg-surface rounded-2xl border border-white/5 overflow-hidden cursor-pointer group hover:border-gg-red/30 transition-all duration-500 hover:shadow-xl hover:shadow-gg-red/5"
      data-testid={`transformation-${index}`}
    >
      {/* Before / After Images */}
      <div className="grid grid-cols-2 relative">
        <div className="relative overflow-hidden">
          <motion.img
            src={item.before_image}
            alt={`${item.client_name} before transformation`}
            className="w-full aspect-[4/5] object-cover"
            animate={{ scale: hovered ? 1.05 : 1 }}
            transition={{ duration: 0.6 }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <motion.span
            animate={{ x: hovered ? 4 : 0 }}
            className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-sm text-white text-[9px] font-heading uppercase tracking-[0.2em] px-2.5 py-1 rounded-md border border-white/10"
          >Before</motion.span>
        </div>

        {/* Divider */}
        <motion.div
          className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-gg-red z-10"
          animate={{ scaleY: hovered ? 1 : 0.6, opacity: hovered ? 1 : 0.5 }}
          transition={{ duration: 0.4 }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-gg-red flex items-center justify-center"
          animate={{ scale: hovered ? 1.1 : 0.9, rotate: hovered ? 0 : -90 }}
          transition={{ duration: 0.4, type: "spring" }}
        >
          <ChevronRight size={14} className="text-white" />
        </motion.div>

        <div className="relative overflow-hidden">
          <motion.img
            src={item.after_image}
            alt={`${item.client_name} after transformation`}
            className="w-full aspect-[4/5] object-cover"
            animate={{ scale: hovered ? 1.05 : 1 }}
            transition={{ duration: 0.6 }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <motion.span
            animate={{ x: hovered ? 4 : 0 }}
            className="absolute bottom-3 left-3 bg-gg-red text-white text-[9px] font-heading uppercase tracking-[0.2em] px-2.5 py-1 rounded-md"
          >After</motion.span>
        </div>
      </div>

      {/* Info */}
      <div className="p-5 relative">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-base font-bold uppercase tracking-wider text-white group-hover:text-gg-red transition-colors duration-300">{item.client_name}</h3>
            {item.duration && (
              <div className="flex items-center gap-1.5 mt-1">
                <Clock size={12} className="text-gg-red" />
                <span className="text-gg-red text-xs font-heading uppercase tracking-wide">{item.duration}</span>
              </div>
            )}
          </div>
          <motion.div
            animate={{ x: hovered ? 4 : 0, opacity: hovered ? 1 : 0.4 }}
            className="mt-1"
          >
            <Flame size={18} className="text-gg-red" />
          </motion.div>
        </div>
        {item.description && (
          <motion.p
            animate={{ opacity: hovered ? 1 : 0.6 }}
            className="text-zinc-400 text-xs mt-3 leading-relaxed"
          >{item.description}</motion.p>
        )}
        <motion.div
          className="h-[2px] bg-gg-red rounded-full mt-4"
          animate={{ width: hovered ? "100%" : "0%" }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </motion.div>
  );
}

export default function TransformationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/transformations`).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="transformations-page">
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-heading text-gg-red text-xs uppercase tracking-[0.3em]">Real Results</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-heading text-xl sm:text-2xl md:text-3xl font-extrabold uppercase tracking-tight mt-1">Client <span className="text-gg-red">Transformations</span></motion.h1>
        </div>
      </div>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-gg-red border-t-transparent rounded-full animate-spin" /></div>
          ) : items.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
              <Flame size={40} className="text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-sm mb-4">Transformations coming soon! Stay tuned for real client before & after results.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-gg-red text-sm font-heading uppercase tracking-widest hover:text-white transition-colors group">
                Start Your Transformation <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {items.map((item, i) => (
                  <TransformationCard key={item.id} item={item} index={i} />
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mt-12"
              >
                <p className="text-zinc-500 text-sm mb-4">Ready to be our next success story?</p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-gg-red hover:bg-gg-red-hover text-white px-6 py-3 rounded-xl font-heading text-xs uppercase tracking-widest transition-all duration-300 hover:shadow-lg hover:shadow-gg-red/20 group">
                  Start Your Transformation <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
